import styled, { css, keyframes } from 'styled-components';

import { Text } from '../../../../components/Text/Text';
import { hoverWash } from '../../../../theme/mixins';
import { ANIM, ART_STYLE, COLORS, GRADIENTS, MOBILE, REDUCED_MOTION, SPACE } from '../../../../theme/tokens';

const rise = keyframes`
  from {
    opacity: 0;
    transform: translateY(${ART_STYLE.FOOT_RISE_PX}px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

/** A sticky footer for the ingredients column: pinned to the bottom of the screen while the column is in view,
 *  on the panel's own surface so the ingredients scroll away beneath it. `margin-top: auto` keeps it at the foot
 *  of the column when the samples beside it are taller. */
export const Bar = styled.div<{ $sheet: boolean; $veiled: boolean }>`
  position: sticky;
  bottom: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${SPACE[8]};
  margin-top: auto;
  /* As wide as the open accordion section above (it reaches ${SPACE[12]} past the column on each side), so nothing
     scrolling beneath shows at the edges; the padding keeps the button where it was. */
  margin-left: -${SPACE[12]};
  margin-right: -${SPACE[12]};
  padding: ${SPACE[16]} ${SPACE[12]};
  border-top: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceCard};
  /* It mounts when it has something to offer (a new style, or ingredients changed since the last extraction): it
     rises into view then. */
  animation: ${rise} ${ART_STYLE.FOOT_ENTER_MS}ms ${ART_STYLE.FOOT_EASE} both;

  ${REDUCED_MOTION} {
    animation: none;
  }

  /* While ingredients scroll beneath it, they fade into the bar instead of being cut at its edge (the sheet's
     scroll fade; a real mask cannot follow a bar pinned to the screen while the page scrolls). */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    /* Down over the border row itself, redrawing the hairline as its own bottom edge: stopping short of the border
       left a 1-2px band (sub-pixel sticky positions) where the ingredients showed through unveiled. */
    bottom: 100%;
    height: ${ART_STYLE.FOOT_FADE_PX + 1}px;
    background: ${GRADIENTS.extractFootScrim};
    box-shadow: inset 0 -1px 0 ${COLORS.hairline};
    pointer-events: none;
    opacity: ${({ $veiled }) => ($veiled ? 1 : 0)};
    transition: opacity ${ART_STYLE.FOOT_FADE_MS}ms ${ANIM.EASE};
  }

  /* In the ingredients sheet it is the sheet footer's content: the footer draws the hairline and pins it, and the sheet
     scrolls its own body, so the bar is a plain column across the footer's width. */
  ${({ $sheet }) =>
    $sheet &&
    css`
      position: static;
      flex: 1 1 auto;
      min-width: ${SPACE[0]};
      margin: ${SPACE[0]};
      padding: ${SPACE[0]};
      border-top: none;
      background: none;
      animation: none;

      &::after {
        content: none;
      }
    `}
`;

export const ChangedHint = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: ${SPACE[8]};
  font-size: 12.5px;
  line-height: 1.45;
  color: ${COLORS.accent};

  svg {
    flex: 0 0 auto;
    margin-top: ${SPACE[2]};
  }
`;

/* "Extract style": a Lumio action, so it carries the solid Lumio glyph; a quiet tag carries its cost. */
export const ExtractButton = styled.button`
  ${hoverWash}
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  height: 44px;
  padding: 0 ${SPACE[16]} 0 ${SPACE[16]};
  border-radius: 999px;
  border: 1px solid ${COLORS.accent};
  background: ${COLORS.surfaceFaint};
  color: ${COLORS.textBright};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
    border-color: ${COLORS.hairline};
  }

  justify-content: center;
  width: 100%;

  ${MOBILE} {
    height: 48px;
  }
`;

export const CostTag = styled(Text)`
  padding: ${SPACE[2]} ${SPACE[8]};
  border-radius: 999px;
  background: ${COLORS.surfaceMuted};
  color: ${COLORS.textMuted};
  font-size: 11px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
`;

export const FootHint = styled(Text)`
  text-align: center;
  font-size: 12px;
  color: ${COLORS.textFaint};
`;

/** Marks the end of the ingredients column: out of view means the bar is pinned with ingredients beneath it. */
export const EndSentinel = styled.span`
  display: block;
  height: 1px;
  /* -1px: it takes no room in the column. */
  margin-top: -1px;
  pointer-events: none;
`;
